import { useCallback, useState } from 'react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { searchUserByNickname } from '@/client/admin.client';
import { MailRewardPicker } from '@/components/admin/MailRewardPicker';
import type { RewardOption } from '@/components/admin/mail/reward-options';
import { dedupeRecipients, parseRecipientPaste } from '@/components/admin/mail/recipients';
import { serializeRewardRows } from '@/components/admin/mail/serialize';
import type { RewardGroup } from './compile-groups';

interface RewardGroupBuilderProps {
  options: RewardOption[];
  groups: RewardGroup[];
  onChange: (groups: RewardGroup[]) => void;
  disabled?: boolean;
}

function shortAccount(addr: string): string {
  return addr.length > 10 ? `${addr.slice(0, 6)}…${addr.slice(-4)}` : addr;
}

interface GroupCardProps {
  index: number;
  group: RewardGroup;
  options: RewardOption[];
  disabled?: boolean;
  onPatch: (patch: Partial<RewardGroup>) => void;
  onRemove: () => void;
}

function GroupCard({ index, group, options, disabled, onPatch, onRemove }: GroupCardProps) {
  const [pasteText, setPasteText] = useState('');
  const [pasteNote, setPasteNote] = useState<string | null>(null);
  const [resolving, setResolving] = useState(false);

  const serialized = serializeRewardRows(group.rewards);

  const handleAddRecipients = useCallback(async () => {
    const { addresses, nicknames } = parseRecipientPaste(pasteText);
    const resolved: string[] = [...addresses];
    const unresolved: string[] = [];
    setResolving(true);
    try {
      for (const nick of nicknames) {
        try {
          const r = await searchUserByNickname(nick);
          if (r.account) resolved.push(r.account);
          else unresolved.push(nick);
        } catch {
          unresolved.push(nick);
        }
      }
      onPatch({ accounts: dedupeRecipients([...group.accounts, ...resolved]) });
      setPasteText('');
      setPasteNote(
        unresolved.length ? `해석 실패: ${unresolved.join(', ')}` : `${resolved.length}명 추가됨`
      );
    } finally {
      setResolving(false);
    }
  }, [pasteText, group.accounts, onPatch]);

  const removeAccount = (account: string) =>
    onPatch({ accounts: group.accounts.filter((a) => a !== account) });

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle className="text-sm">그룹 {index + 1}</CardTitle>
        <Button type="button" variant="ghost" size="sm" onClick={onRemove} disabled={disabled}>
          그룹 삭제
        </Button>
      </CardHeader>
      <CardContent className="flex flex-col gap-4">
        <div className="flex flex-col gap-1">
          <Label>보상</Label>
          <MailRewardPicker
            options={options}
            rows={group.rewards}
            onChange={(rows) => onPatch({ rewards: rows })}
            disabled={disabled}
          />
          {!serialized.ok && (
            <p className="text-xs text-destructive">{serialized.errors.join('; ')}</p>
          )}
        </div>

        <div className="flex flex-col gap-2">
          <Label>대상 ({group.accounts.length})</Label>
          <div className="flex max-h-32 flex-wrap gap-1 overflow-y-auto rounded-md border p-2">
            {group.accounts.length === 0 ? (
              <span className="text-xs text-muted-foreground">
                주소/닉네임을 붙여넣어 추가하세요.
              </span>
            ) : (
              group.accounts.map((a) => (
                <Badge key={a} variant="secondary" className="font-mono text-[10px]">
                  {shortAccount(a)}
                  <button
                    type="button"
                    className="ml-1"
                    onClick={() => removeAccount(a)}
                    disabled={disabled}
                  >
                    ✕
                  </button>
                </Badge>
              ))
            )}
          </div>
          <div className="flex gap-2">
            <Textarea
              value={pasteText}
              onChange={(e) => setPasteText(e.target.value)}
              placeholder="주소 또는 닉네임을 붙여넣기 (줄바꿈/콤마 구분)"
              className="text-xs"
              rows={2}
              disabled={disabled}
            />
            <Button
              type="button"
              variant="secondary"
              onClick={() => void handleAddRecipients()}
              disabled={disabled || resolving || !pasteText.trim()}
            >
              추가
            </Button>
          </div>
          {pasteNote && <p className="text-xs text-muted-foreground">{pasteNote}</p>}
        </div>
      </CardContent>
    </Card>
  );
}

export function RewardGroupBuilder({
  options,
  groups,
  onChange,
  disabled,
}: RewardGroupBuilderProps) {
  const setGroup = (i: number, patch: Partial<RewardGroup>) =>
    onChange(groups.map((g, idx) => (idx === i ? { ...g, ...patch } : g)));
  const addGroup = () => onChange([...groups, { rewards: [], accounts: [] }]);
  const removeGroup = (i: number) => onChange(groups.filter((_, idx) => idx !== i));

  return (
    <div className="flex flex-col gap-4">
      {groups.map((group, i) => (
        <GroupCard
          // biome-ignore lint/suspicious/noArrayIndexKey: groups are positional, no stable id
          key={i}
          index={i}
          group={group}
          options={options}
          disabled={disabled}
          onPatch={(patch) => setGroup(i, patch)}
          onRemove={() => removeGroup(i)}
        />
      ))}
      <Button type="button" variant="outline" onClick={addGroup} disabled={disabled}>
        + 그룹 추가
      </Button>
    </div>
  );
}

export default RewardGroupBuilder;
